import React, {useEffect, useState} from 'react';
import clinicService from '../../../../../services/clinic.service';
import {Collapse, Pagination, Table, Alert} from 'react-bootstrap';
import Loading from "../../../../common/Loading";

function CommentsTable({clinicid}) {
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);
    const [copiedItems, setCopiedItems] = useState({});

    const [pagination, setPagination] = useState({
        page: 0,
        size: 5,
        totalElements: 0,
        totalPages: 0
    });

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const getComments = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const res = await clinicService.getReputationComments(
                    clinicid,
                    pagination.page,
                    pagination.size,
                    {signal: controller.signal}
                );

                if (isMounted) {
                    setComments(res.data.content);
                    setPagination(prev => ({
                        ...prev,
                        totalElements: res.data.totalElements,
                        totalPages: res.data.totalPages
                    }));

                    if (res.data.content.length === 0) {
                        setError('No comments found.');
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError' && isMounted) {
                    console.error("Fetch error:", err);
                    setError('Unable to load comments. Please try again.');
                    setComments([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        getComments();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [clinicid, pagination.page, pagination.size]);

    const toggleRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id]
        );
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({...prev, page: page - 1}));
    };

    const handleSizeChange = (e) => {
        setPagination(prev => ({...prev, size: Number(e.target.value), page: 0}));
    };

    const copyToClipboard = (text, itemType, itemId) => {
        navigator.clipboard.writeText(text).then(() => {
            // Set the copied item with a unique key
            const key = `${itemType}-${itemId}`;
            setCopiedItems(prev => ({...prev, [key]: true}));

            // Reset after 2 seconds
            setTimeout(() => {
                setCopiedItems(prev => ({...prev, [key]: false}));
            }, 2000);
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'SENT':
                return <div className="badge status-completed text-success px-2 py-1 small">
                    <i className="bi bi-check-circle text-success"></i> Sent
                </div>;
            case 'PENDING':
                return <div className="badge status-pending text-warning px-2 py-1 small">
                    <i className="bi bi-clock text-warning"></i> Pending
                </div>;
            case 'ERROR':
                return <div className="badge status-incomplete text-danger px-2 py-1 small">
                    <i className="bi bi-x-circle text-danger"></i> Error
                </div>;
            default:
                return <div className="badge bg-secondary px-2 py-1 small">{status}</div>;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const truncateNotes = (text, length = 100) => {
        if (!text) return '';
        return text.length > length
            ? `${text.substring(0, length)}...`
            : text;
    };

    // Generate pagination items
    const renderPaginationItems = () => {
        const items = [];
        const totalPages = pagination.totalPages;
        const currentPage = pagination.page + 1;

        // Always show first page
        items.push(
            <Pagination.Item
                key={1}
                active={1 === currentPage}
                onClick={() => handlePageChange(1)}
                className="small"
            >
                1
            </Pagination.Item>
        );

        // Calculate start and end of page range to show
        let startPage = Math.max(2, currentPage - 1);
        let endPage = Math.min(totalPages - 1, currentPage + 1);

        // Add ellipsis if needed after first page
        if (startPage > 2) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" className="small"/>);
        }

        // Add middle pages
        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                    className="small"
                >
                    {page}
                </Pagination.Item>
            );
        }

        // Add ellipsis if needed before last page
        if (endPage < totalPages - 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" className="small"/>);
        }

        // Always show last page if there is more than one page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage}
                    onClick={() => handlePageChange(totalPages)}
                    className="small"
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    return (
        <div className="container-fluid py-4">
            <div className="card shadow-none">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="card-title mb-0 fw-semibold  text-primary">
                            <i className="bi bi-chat-text me-2"></i>
                            Patient Comments
                        </h5>
                        <div className="d-flex align-items-center">
                            <span className="me-2 small text-muted">Show:</span>
                            <select
                                className="form-select form-select-sm w-auto small"
                                value={pagination.size}
                                onChange={handleSizeChange}
                            >
                                {[5, 10, 20, 50].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {isLoading ? (
                        <Loading/>
                    ) : (
                        <>
                            <div className="table-responsive small">
                                <Table striped bordered hover className="mb-0">
                                    <thead className="table-light">
                                    <tr>
                                        <th width="20%"><i className="bi bi-person"></i> Patient</th>
                                        <th width="40%"><i className="bi bi-chat"></i> Notes</th>
                                        <th width="10%"><i className="bi bi-check-circle"></i> Status</th>
                                        <th width="20%"><i className="bi bi-calendar"></i> Date</th>
                                        <th width="10%"><i className="bi bi-gear"></i> Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {comments.length > 0 ? (
                                        comments.map(comment => (
                                            <React.Fragment key={comment.id}>
                                                <tr>
                                                    <td>
                                                        <div className="fw-semibold">
                                                            {comment.firstName} {comment.lastName}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div>{truncateNotes(comment.notes)}</div>
                                                    </td>
                                                    <td>
                                                        {getStatusBadge(comment.status)}
                                                    </td>
                                                    <td>
                                                        <div className="small">{formatDate(comment.createdAt)}</div>
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className={`btn btn-sm ${expandedRows.includes(comment.id) ? 'btn-outline-primary' : 'btn-outline-primary'}`}
                                                            onClick={() => toggleRow(comment.id)}
                                                            title={expandedRows.includes(comment.id) ? 'Collapse' : 'Expand'}
                                                        >
                                                            <i className={`bi ${expandedRows.includes(comment.id) ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {expandedRows.includes(comment.id) && (
                                                    <tr>
                                                        <td colSpan="5" className="p-0 border-top-0">
                                                            <Collapse in={expandedRows.includes(comment.id)}>
                                                                <div className="p-3 bg-light">
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-between">
                                                                        <div className="fw-semibold">Comment Details
                                                                        </div>
                                                                        <div className="">
                                                                            <small className="text-muted me-1">Created
                                                                                At:</small>
                                                                            <small
                                                                                className="font-monospace small">{formatDate(comment.createdAt)}</small>
                                                                        </div>
                                                                    </div>
                                                                    <hr/>
                                                                    <div className="row">
                                                                        <div className="col-md-6">
                                                                            <div
                                                                                className="mb-2 d-flex align-items-center">
                                                                                <small
                                                                                    className="text-muted d-block me-2">Comment
                                                                                    ID:</small>
                                                                                <span
                                                                                    className="font-monospace small me-2">{comment.id}</span>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-secondary p-1"
                                                                                    onClick={() => copyToClipboard(comment.id, 'comment', comment.id)}
                                                                                    title="Copy Comment ID"
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`comment-${comment.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </button>
                                                                            </div>
                                                                            <div
                                                                                className="mb-2 d-flex align-items-center">
                                                                                <small
                                                                                    className="text-muted d-block me-2">Patient
                                                                                    ID:</small>
                                                                                <span
                                                                                    className="font-monospace small me-2">{comment.patientId}</span>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-secondary p-1"
                                                                                    onClick={() => copyToClipboard(comment.patientId, 'patient', comment.id)}
                                                                                    title="Copy Patient ID"
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`patient-${comment.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div
                                                                                className="mb-2 d-flex align-items-center">
                                                                                <small
                                                                                    className="text-muted d-block me-2">Clinic
                                                                                    ID:</small>
                                                                                <span
                                                                                    className="font-monospace small me-2">{comment.clinicId}</span>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-secondary p-1"
                                                                                    onClick={() => copyToClipboard(comment.clinicId, 'clinic', comment.id)}
                                                                                    title="Copy Clinic ID"
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`clinic-${comment.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </button>
                                                                            </div>
                                                                            <div className="mb-2">
                                                                                <small
                                                                                    className="text-muted me-3">Status:</small>
                                                                                <span
                                                                                    className="fw-semibold">{getStatusBadge(comment.status)}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="mt-3">
                                                                        <h6 className="fw-semibold mb-2">Full Notes</h6>
                                                                        <code>
                                                                            <pre
                                                                                className="mb-0 p-2 bg-white rounded border">{comment.notes}</pre>
                                                                        </code>
                                                                    </div>
                                                                </div>
                                                            </Collapse>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">
                                                <Alert variant="danger" className="mb-0 small">
                                                    <i className="bi bi-info-circle me-2"></i>
                                                    {error || "No comments found."}
                                                </Alert>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </Table>
                            </div>

                            {comments.length > 0 && pagination.totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted small">
                                        Showing {Math.min(pagination.size, pagination.totalElements - (pagination.page * pagination.size))} of {pagination.totalElements} comments
                                    </div>

                                    <Pagination className="mb-0 small">
                                        <Pagination.First
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(1)}
                                            className="small"
                                        />
                                        <Pagination.Prev
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(pagination.page)}
                                            className="small"
                                        />

                                        {renderPaginationItems()}

                                        <Pagination.Next
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.page + 2)}
                                            className="small"
                                        />
                                        <Pagination.Last
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.totalPages)}
                                            className="small"
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CommentsTable;